// Regional security: bandit raids, sabotage, theft (DESIGN §12). Runs daily after production.
import { chance, range, int, nextFloat } from '../core/rng.js';
import { BASE_HAZARD } from '../data/regions.js';
import { eqDef, fieldDef, regionDef, regionRisk, stat, eraDollars, unitPrice, isAvailable, yf } from './defs.js';
import { injureCrew, activeRigCrew, findSite, unassignGuard, removeFromAssignments, roleName } from './crew.js';
import { clamp, notify, log, spend, nextId, fmt$ } from './util.js';
import { addItem } from '../core/state.js';
import { adjustSecurity, attackMult, extraHazards, applyRebelAttack, startKidnapping, startLaborStrike, regionM2View, M2_FROM } from './m2/politics.js';
import { gaugerTheftMult } from './automation.js';

// Extensible incident enum. `m1: true` types are live; the rest are M2 stubs.
export const INCIDENT_TYPES = {
  bandit_raid: { name: 'Bandit raid', hazard: 'bandits', m1: true },
  sabotage: { name: 'Sabotage', hazard: 'sabotage', m1: true },
  theft: { name: 'Pipeline tap / theft', hazard: 'theft', m1: true },
  // M2 (live from 1901, sim/m2/politics.js): rebel attacks use the defend/payoff/evacuate decision; kidnapping and
  // labor strikes have their own decisions (kinds 'kidnapping', 'laborStrike').
  rebel_attack: { name: 'Rebel attack', hazard: 'uprising', m1: false, m2: true },
  kidnapping: { name: 'Kidnapping', hazard: 'kidnapping', m1: false, m2: true },
  piracy: { name: 'Pirate boarding', hazard: 'piracy', m1: false, m2: true, offshore: true },
  labor_strike: { name: 'Labor unrest', hazard: 'strike', m1: false, m2: true },
  expropriation: { name: 'Expropriation', hazard: 'expropriation', m1: false }, // regional annual roll (m2/politics.js), not per-site
};
export const CHOICES = ['defend', 'payoff', 'evacuate', 'fight', 'forces', 'citadel']; // last three: piracy
const HAZARD_TO_TYPE = Object.fromEntries(Object.entries(INCIDENT_TYPES).map(([k, v]) => [v.hazard, k]));

export function regionOfField(fieldId) { return fieldDef(fieldId)?.regionId ?? null; }

export function effectiveRisk(state, regionId) {
  const r = regionRisk(regionId, yf(state));
  const mod = state.regions[regionId]?.securityMod ?? 0;
  // M2: temporary drops after rebel attacks, occupation caps security at 0.1 (m2/politics.js)
  return { ...r, security: adjustSecurity(state, regionId, clamp(r.security + mod, 0, 1)) };
}

// Incident types that can strike a site in this region now (M1 types always; M2 types from 1901).
export function liveIncidentTypes(state, regionId) {
  const rdef = regionDef(regionId);
  const y = yf(state);
  const hz = [...(rdef?.hazards ?? []), ...(y >= M2_FROM ? extraHazards(regionId, y).filter((h) => !rdef?.hazards?.includes(h)) : [])];
  return hz.map((h) => HAZARD_TO_TYPE[h]).filter((t) => t && (INCIDENT_TYPES[t].m1 || (INCIDENT_TYPES[t].m2 && y >= M2_FROM)));
}

// Sites: every well with a rig on it or producing/gushing. Site id = well id.
export function sites(state) {
  return Object.values(state.wells).filter((w) => w.rigId || ['PRODUCING', 'GUSHER', 'SHUT_IN'].includes(w.status));
}

export function siteGuards(state, well) {
  const rig = well.rigId ? state.rigs[well.rigId] : null;
  // perf fast path: most sites have no guards at all
  if (!well.security?.guards?.length && !rig?.security?.guards?.length && !state.fields[well.fieldId].security?.guards?.length) return { own: [], fieldGuards: [] };
  const ids = new Set([...(well.security?.guards ?? [])]);
  for (const g of rig?.security?.guards ?? []) ids.add(g);
  const f = state.fields[well.fieldId];
  const fieldGuards = (f.security?.guards ?? []).map((id) => state.crew[id]).filter((c) => c && c.status === 'active' && !c.injuredUntil);
  const own = [...ids].map((id) => state.crew[id]).filter((c) => c && c.status === 'active' && !c.injuredUntil);
  return { own, fieldGuards };
}

const strength = (c) => (c.skill / 100 + (c.traits?.includes('crack_shot') ? 0.2 : 0)) * 1.2;
// ctx (optional, tickSecurity): per-tick memo { sitesByField: Map } — same values, computed once per tick
export function guardFactor(state, well, risk = null, ctx = null) {
  const regionId = regionOfField(well.fieldId);
  risk ??= effectiveRisk(state, regionId);
  const { own, fieldGuards } = siteGuards(state, well);
  const threat = 0.5 + 1.5 * (1 - risk.security);
  // field-wide guards are spread across every site in the field
  const nSites = fieldGuards.length ? Math.max(1, ctx ? sitesOnField(state, ctx, well.fieldId) : sites(state).filter((w) => w.fieldId === well.fieldId).length) : 1;
  const g = own.reduce((a, c) => a + strength(c), 0) + fieldGuards.reduce((a, c) => a + strength(c), 0) / nSites;
  let gf = Math.min(0.9, g / threat);
  const forts = [well.security?.fort, well.rigId ? state.rigs[well.rigId]?.security?.fort : null].filter(Boolean);
  const fieldFort = state.fields[well.fieldId]?.security?.fort; // M2 armored car patrol: `mobile`, covers every site in the field
  if (fieldFort && stat(eqDef(fieldFort), 'mobile', false) && !forts.includes(fieldFort)) forts.push(fieldFort);
  for (const f of forts) gf += stat(eqDef(f), 'guardFactorBonus', 0.15);
  const reg = state.regions[regionId];
  if (reg?.mercUntil > state.day) gf += 0.3;
  return clamp(gf, 0, 0.95);
}

export function siteVisibility(state, well) {
  const rich = well.status === 'GUSHER' || (well.prod?.rate ?? 0) > 100;
  return 1 + 0.5 * (rich ? 1 : 0);
}

function sitesOnField(state, ctx, fieldId) {
  if (!ctx.sitesByField) { ctx.sitesByField = new Map(); for (const w of sites(state)) ctx.sitesByField.set(w.fieldId, (ctx.sitesByField.get(w.fieldId) ?? 0) + 1); }
  return ctx.sitesByField.get(fieldId) ?? 0;
}

export function attackChance(state, well, ctx = null) {
  const regionId = regionOfField(well.fieldId);
  const rdef = regionDef(regionId);
  if (!rdef) return 0;
  // ctx.region: per-tick memo of the region-level terms (pure in the state, which only startIncident changes)
  let rc = ctx?.region.get(regionId);
  if (!rc) {
    const live = liveIncidentTypes(state, regionId);
    rc = live.length ? { live: true, risk: effectiveRisk(state, regionId), mult: attackMult(state, regionId) } : { live: false };
    ctx?.region.set(regionId, rc);
  }
  if (!rc.live) return 0;
  const risk = rc.risk;
  const calm = state.regions[regionId]?.calm ?? 0;
  return BASE_HAZARD * (1 - risk.security) ** 2 * siteVisibility(state, well) * (1 - guardFactor(state, well, risk, ctx)) * (1 - calm) * rc.mult;
}

export function tickSecurity(state) {
  // auto-resolve stale decisions (player not answering with auto-pause off)
  for (const inc of Object.values(state.incidents)) {
    if (inc.status === 'pending' && state.day >= inc.deadline) resolveImpl(state, inc.id, inc.type === 'piracy' ? 'citadel' : inc.guardFactor > 0.3 ? 'defend' : 'evacuate', true);
  }
  // perf: pending sites, region terms and site counts computed once; any new incident invalidates them all
  let pending = null, ctx = { region: new Map(), sitesByField: null };
  for (const well of sites(state)) {
    if (!pending) { pending = new Set(); for (const i of Object.values(state.incidents)) if (i.status === 'pending') pending.add(i.siteId); }
    if (pending.has(well.id)) continue;
    const p = attackChance(state, well, ctx);
    if (p > 0 && chance(state, p)) { startIncident(state, well); pending = null; ctx = { region: new Map(), sitesByField: null }; }
  }
  // prune old resolved incidents
  for (const inc of Object.values(state.incidents)) if (inc.status !== 'pending' && state.day - inc.day > 730) delete state.incidents[inc.id];
}

export function startIncident(state, well, forcedType = null) {
  const regionId = regionOfField(well.fieldId);
  const rdef = regionDef(regionId);
  // offshore sites only see what can reach them by sea; pirates only come for offshore sites
  const offshore = isOffshoreSite(well);
  const live = liveIncidentTypes(state, regionId).filter((t) => (offshore ? SEA_TYPES.has(t) : !INCIDENT_TYPES[t]?.offshore));
  if (!forcedType && !live.length) return null;
  let type = forcedType ?? (live.includes('piracy') && chance(state, 0.6) ? 'piracy' : live[Math.floor(nextFloat(state) * live.length)]) ?? 'theft';
  if (type === 'piracy') return startPiracy(state, well, regionId);
  // M2: kidnapping and labor strikes run their own decisions (m2/politics.js)
  if (type === 'kidnapping') { const k = startKidnapping(state, well); if (k) return k; type = live.includes('rebel_attack') ? 'rebel_attack' : 'theft'; }
  if (type === 'labor_strike') { startLaborStrike(state, well); return null; }
  const risk = effectiveRisk(state, regionId);
  const vis = siteVisibility(state, well);
  const gf = guardFactor(state, well, risk);
  const id = nextId(state, 'x');
  const payoff = Math.round(eraDollars(state, (type === 'theft' ? 40 : 80) * vis * (1 + risk.corruption), 1859));
  const inc = {
    id, type, siteId: well.id, wellId: well.id, fieldId: well.fieldId, regionId, day: state.day, deadline: state.day + 3,
    status: 'pending', guardFactor: Math.round(gf * 100) / 100, defendWinPct: Math.round(100 * defendWin(gf)), payoffCost: payoff,
  };
  state.incidents[id] = inc;
  const reg = (state.regions[regionId] ??= { securityMod: 0, incidents: [], partner: null, bribe: 0, insurance: null });
  reg.incidents.push({ day: state.day, type, siteId: well.id });
  if (reg.incidents.length > 50) reg.incidents.shift();
  state.company.stats.incidents++;
  const decision = {
    kind: 'incident', id, incidentId: id, day: state.day,
    title: INCIDENT_TYPES[type].name, text: incidentText(state, inc, well),
    options: [
      { choice: 'defend', label: `Defend (${inc.defendWinPct}% win)`, winPct: inc.defendWinPct },
      { choice: 'payoff', label: `Pay off (${fmt$(payoff)})`, cost: payoff },
      { choice: 'evacuate', label: 'Evacuate the crew', },
    ],
  };
  queueDecision(state, decision);
  notify(state, 'attack', { wellId: well.id, fieldId: well.fieldId, regionId, incidentId: id, incidentType: type, pause: true, text: decision.text, level: 'warn' });
  return inc;
}

const SEA_TYPES = new Set(['piracy', 'sabotage', 'kidnapping', 'expropriation']);
const isOffshoreSite = (well) => !!(well.offshore || (fieldDef(well.fieldId)?.waterDepthFt ?? 0) > 0);
const hasMaritimeSecurity = (state) => !!(state.research?.done?.maritime_security || state.research?.upgrades?.maritime_security || Object.values(state.items ?? {}).some((i) => i.defId === 'maritime_security'));
// Special forces / navy rescue price, in the money of the day (about $400k in 2010).
export const forcesCost = (state) => Math.round(eraDollars(state, 400000, 2010));
// Fight-back odds: your guards' strength against pirates in fast boats; a maritime security escort helps a lot.
export function piracyFightPct(state, well) {
  const gf = guardFactor(state, well, effectiveRisk(state, regionOfField(well.fieldId)));
  return Math.round(100 * clamp(0.1 + 0.7 * gf + (hasMaritimeSecurity(state) ? 0.25 : 0), 0.05, 0.9));
}

// Pirates in fast skiffs board an offshore rig or platform: fight back, call in special forces, or lock down.
function startPiracy(state, well, regionId) {
  const id = nextId(state, 'x');
  const fightPct = piracyFightPct(state, well), cost = forcesCost(state);
  const inc = { id, type: 'piracy', siteId: well.id, wellId: well.id, fieldId: well.fieldId, regionId, day: state.day, deadline: state.day + 1, status: 'pending', fightPct, forcesCost: cost, guardFactor: 0, defendWinPct: fightPct, payoffCost: 0 };
  state.incidents[id] = inc;
  const reg = (state.regions[regionId] ??= { securityMod: 0, incidents: [], partner: null, bribe: 0, insurance: null });
  reg.incidents.push({ day: state.day, type: 'piracy', siteId: well.id });
  if (reg.incidents.length > 50) reg.incidents.shift();
  state.company.stats.incidents++;
  well.attacked = { day: state.day, type: 'piracy', boarding: true, damaged: false, fire: false };
  const place = fieldDef(well.fieldId)?.name ?? well.fieldId;
  const text = `Pirates in fast skiffs are boarding ${well.name} at ${place}! Armed men are coming up the ladders. The crew can fight, hold out in the citadel, or you can call the navy's special forces.`;
  queueDecision(state, {
    kind: 'incident', id, incidentId: id, day: state.day, title: 'Pirate boarding', text,
    options: [
      { choice: 'fight', label: `Fight back (${fightPct}% chance)`, winPct: fightPct, desc: hasMaritimeSecurity(state) ? 'Your maritime security escort is aboard.' : 'Your guards against armed men in fast boats.' },
      { choice: 'forces', label: `Call in the special forces (${fmt$(cost)})`, cost, desc: 'Navy / marine boarding team retakes the rig. Very likely to work.' },
      { choice: 'citadel', label: 'Lock down in the citadel', desc: 'Nobody gets hurt, but they loot the rig and work stops for a few days.' },
    ],
  });
  notify(state, 'attack', { wellId: well.id, fieldId: well.fieldId, regionId, incidentId: id, incidentType: 'piracy', pause: true, text, level: 'warn' });
  return inc;
}

function resolvePiracy(state, inc, well, choice) {
  if (choice === 'defend') choice = 'fight'; else if (choice === 'payoff' || choice === 'evacuate') choice = 'citadel';
  inc.choice = choice;
  const rig = well.rigId ? state.rigs[well.rigId] : null;
  const crew = siteCrew(state, well), { own, fieldGuards } = siteGuards(state, well), guards = [...own, ...fieldGuards];
  const loot = () => { const v = Math.round(eraDollars(state, 60000, 2010) * (0.5 + nextFloat(state))); spend(state, v, 'security', false); return v; };
  const halt = (days) => { if (well.prod) well.prod.forcedUntil = Math.max(well.prod.forcedUntil ?? 0, state.day + days); };
  let msg;
  if (choice === 'forces' && state.company.cash < inc.forcesCost) { choice = 'citadel'; msg = "Couldn't pay for the special forces. "; }
  if (choice === 'forces') {
    spend(state, inc.forcesCost, 'security');
    if (chance(state, 0.9)) {
      inc.outcome = 'rescued'; state.company.reputation = Math.min(100, (state.company.reputation ?? 30) + 2);
      msg = `Special forces fast-roped onto ${well.name} and took the pirates. Crew safe; the rig is yours again.`;
      well.attacked = { day: state.day, type: 'piracy', damaged: false, fire: false, rescued: true };
    } else {
      inc.outcome = 'firefight';
      const v = [...crew, ...guards]; if (v.length) injureCrew(state, v[Math.floor(nextFloat(state) * v.length)], 'was hit in the crossfire when the special forces went in', { fatalChance: 0.3, wellId: well.id, accident: 'piracy' });
      if (rig) rig.condition = Math.max(0, rig.condition - 10);
      msg = `The special forces retook ${well.name} after a firefight. One of your people was hit.`;
      well.attacked = { day: state.day, type: 'piracy', damaged: true, fire: false };
    }
  } else if (choice === 'fight') {
    if (chance(state, inc.fightPct / 100)) {
      inc.outcome = 'repelled'; for (const g of guards) { g.xp += 25; g.morale = Math.min(100, g.morale + 6); }
      if ([...guards, ...crew].length && chance(state, 0.25)) { const v = [...guards, ...crew]; injureCrew(state, v[Math.floor(nextFloat(state) * v.length)], 'was wounded driving the pirates off', { fatalChance: 0.1, wellId: well.id, accident: 'piracy' }); }
      msg = `The crew drove the pirates back into their skiffs at ${well.name}.`;
      well.attacked = { day: state.day, type: 'piracy', damaged: false, fire: false, repelled: true, fought: true };
    } else {
      inc.outcome = 'boarded';
      const v = [...guards, ...crew];
      for (let i = 0; i < Math.min(v.length, 1 + (nextFloat(state) < 0.5 ? 1 : 0)); i++) injureCrew(state, v.splice(Math.floor(nextFloat(state) * v.length), 1)[0], 'was shot when the pirates took the rig', { fatalChance: 0.3, wellId: well.id, accident: 'piracy' });
      if (rig) rig.condition = Math.max(0, rig.condition - (15 + 20 * nextFloat(state)));
      halt(4);
      msg = `The pirates overran ${well.name}: people hurt, ${fmt$(loot())} of kit and cash taken, work stopped.`;
      well.attacked = { day: state.day, type: 'piracy', damaged: true, fire: false, fought: true };
      if (chance(state, 0.35)) { const k = startKidnapping(state, well); if (k) msg += ' They took hostages.'; }
    }
  } else { // citadel
    inc.outcome = 'looted';
    if (rig) rig.condition = Math.max(0, rig.condition - (5 + 10 * nextFloat(state)));
    halt(3);
    msg = (msg ?? '') + `The crew sat it out in the citadel. The pirates stripped ${well.name} of ${fmt$(loot())} in cash, radios and tools, then left. Nobody hurt.`;
    well.attacked = { day: state.day, type: 'piracy', damaged: false, fire: false, looted: true };
  }
  log(state, msg, inc.outcome === 'rescued' || inc.outcome === 'repelled' ? 'info' : 'warn', { wellId: well.id });
  notify(state, 'news', { kind: 'security', wellId: well.id, fieldId: well.fieldId, regionId: inc.regionId, text: msg, level: 'warn' });
  return { ok: true, msg };
}

function incidentText(state, inc, well) {
  const place = fieldDef(well.fieldId)?.name ?? well.fieldId;
  switch (inc.type) {
    case 'bandit_raid': return `Bandits are riding on ${well.name} at ${place}!`;
    case 'sabotage': return `Saboteurs were seen creeping around ${well.name} at ${place}.`;
    case 'theft': return `Thieves are tapping your oil at ${place}.`;
    case 'rebel_attack': return `Armed rebels are riding on ${well.name} at ${place}! They want horses, cash and oil.`;
    default: return `Trouble at ${well.name}.`;
  }
}

const defendWin = (gf) => clamp(0.15 + 0.85 * gf, 0, 0.97);

export function queueDecision(state, d) {
  if (!state.pendingDecision) state.pendingDecision = d;
  else (state.decisionQueue ??= []).push(d);
}

export function popDecision(state, matchId) {
  if (state.pendingDecision && (state.pendingDecision.id === matchId || matchId == null)) {
    state.pendingDecision = state.decisionQueue?.shift() ?? null;
  } else if (state.decisionQueue) {
    state.decisionQueue = state.decisionQueue.filter((d) => d.id !== matchId);
  }
}

export function resolveImpl(state, incidentId, choice, auto = false) {
  const inc = state.incidents[incidentId];
  if (!inc || inc.status !== 'pending') return { ok: false, msg: 'No such incident pending.' };
  if (!CHOICES.includes(choice)) return { ok: false, msg: 'Choose defend, payoff or evacuate.' };
  const well = state.wells[inc.wellId];
  popDecision(state, incidentId);
  inc.status = 'resolved';
  inc.choice = choice;
  inc.resolvedDay = state.day;
  if (!well) return { ok: true, msg: 'The site is gone; nothing happened.' };
  if (inc.type === 'piracy') return resolvePiracy(state, inc, well, choice);
  const crewAtSite = siteCrew(state, well);
  let msg = '';
  if (choice === 'payoff') {
    if (state.company.cash >= inc.payoffCost) {
      spend(state, inc.payoffCost, 'security');
      inc.outcome = 'paid';
      // paying invites more trouble
      const reg = state.regions[inc.regionId];
      if (reg) reg.securityMod = Math.max(-0.2, (reg.securityMod ?? 0) - 0.01);
      msg = `Paid ${fmt$(inc.payoffCost)}. They ride off — for now.`;
      log(state, msg, 'warn', { wellId: well.id });
      return { ok: true, msg };
    }
    choice = 'evacuate';
    msg = "Couldn't afford the payoff. ";
  }
  if (choice === 'defend') {
    const { own, fieldGuards } = siteGuards(state, well);
    const guards = [...own, ...fieldGuards];
    if (chance(state, defendWin(inc.guardFactor))) {
      inc.outcome = 'repelled';
      for (const g of guards) { g.xp += 20; g.morale = Math.min(100, g.morale + 5); }
      if (guards.length && chance(state, 0.15)) injureCrew(state, guards[Math.floor(nextFloat(state) * guards.length)], 'was wounded holding the line');
      msg += `Your people drove them off at ${well.name}.`;
      log(state, msg, 'info', { wellId: well.id });
      return { ok: true, msg };
    }
    msg += 'The defence failed. ';
    const victims = [...guards, ...crewAtSite];
    if (victims.length && chance(state, 0.4)) injureCrew(state, victims[Math.floor(nextFloat(state) * victims.length)], 'was hurt in the fight');
  } else if (choice === 'evacuate') {
    msg += 'Crew pulled back safely. ';
  }
  inc.outcome = 'lost';
  well.attacked = { day: state.day, type: inc.type, damaged: inc.type === 'sabotage' || inc.type === 'rebel_attack', fire: false };
  msg += applyIncidentEffect(state, inc, well, choice !== 'evacuate' ? crewAtSite : []);
  notify(state, 'news', { kind: 'security', wellId: well.id, fieldId: well.fieldId, regionId: inc.regionId, text: msg, level: 'warn' });
  return { ok: true, msg };
}

function siteCrew(state, well) {
  const rig = well.rigId ? state.rigs[well.rigId] : null;
  return rig ? activeRigCrew(state, rig) : [];
}

function applyIncidentEffect(state, inc, well, exposedCrew) {
  const f = state.fields[well.fieldId];
  switch (inc.type) {
    case 'bandit_raid': {
      const roll = nextFloat(state);
      if (exposedCrew.length && chance(state, 0.3)) injureCrew(state, exposedCrew[Math.floor(nextFloat(state) * exposedCrew.length)], 'was beaten by bandits');
      if (roll < 0.5 && f.storage.bbl > 1) {
        const pct = range(state, 0.2, 0.6);
        const lost = f.storage.bbl * pct;
        f.storage.bbl -= lost;
        f.storage.barrels = Math.max(0, Math.floor(f.storage.barrels * (1 - pct)));
        return `Bandits made off with ${Math.round(lost).toLocaleString('en-US')} bbl of oil.`;
      }
      if (roll < 0.8 && state.company.cash > 0) {
        const cash = Math.min(state.company.cash, eraDollars(state, range(state, 50, 300), 1859));
        spend(state, cash, 'security', false);
        return `Bandits looted ${fmt$(cash)} from the site strongbox.`;
      }
      const rig = well.rigId ? state.rigs[well.rigId] : null;
      if (rig && state.items[rig.bitUid] && rig.task?.type !== 'PUMPING') {
        const name = eqDef(state.items[rig.bitUid].defId)?.name ?? 'bit';
        delete state.items[rig.bitUid];
        rig.bitUid = null; well.bitUid = null;
        return `Bandits stole the ${name} off ${rig.name}.`;
      }
      const lineId = Object.keys(state.stock).find((k) => eqDef(k)?.cat === 'line');
      if (lineId) { const take = Math.min(state.stock[lineId].qty, 200); state.stock[lineId].qty -= take; return `Bandits stole ${Math.round(take)} ft of cable.`; }
      return 'Bandits ransacked the camp but found little.';
    }
    case 'sabotage': {
      const rig = well.rigId ? state.rigs[well.rigId] : null;
      let txt = '';
      if (rig) {
        const dmg = range(state, 30, 80);
        rig.condition = Math.max(0, rig.condition - dmg);
        txt += `${rig.name} damaged ${Math.round(dmg)}%. `;
        if (chance(state, 0.2)) { rig.condition = Math.max(0, rig.condition - 30); txt += 'Fire! '; well.attacked.fire = true; well.fire = { day: state.day, untilDay: state.day + 3 }; }
      }
      if (well.prod) {
        const days = int(state, 10, 60);
        well.prod.forcedUntil = state.day + days;
        txt += `${well.name} shut in for ${days} days of repairs.`;
      }
      return txt || 'The saboteurs did little damage.';
    }
    case 'theft': {
      if (f.storage.bbl > 1 && chance(state, 0.5)) {
        const lost = f.storage.bbl * range(state, 0.1, 0.3);
        f.storage.bbl -= lost;
        return `Thieves drained ${Math.round(lost).toLocaleString('en-US')} bbl from storage.`;
      }
      const days = int(state, 30, 90);
      f.theftUntil = state.day + days;
      f.theftPct = Math.round(range(state, 0.05, 0.3) * gaugerTheftMult(state) * 100) / 100; // M2: a gauger on staff −40%
      return `Your shipments are being tapped: ${Math.round(f.theftPct * 100)}% lost for ${days} days.`;
    }
    case 'rebel_attack':
      return applyRebelAttack(state, inc, well, exposedCrew);
    default:
      return '';
  }
}

// ---- actions ----------------------------------------------------------------
export function assignGuardImpl(state, crewId, siteId) {
  const c = state.crew[crewId];
  if (!c || c.status !== 'active') return { ok: false, msg: 'No such crew member.' };
  if (c.role !== 'guard') return { ok: false, msg: `${c.name} is a ${roleName(c.role)}, not a guard.` };
  if (siteId == null) { removeFromAssignments(state, c); return { ok: true, msg: `${c.name} back at the bunkhouse.` }; }
  const site = findSite(state, siteId);
  if (!site) return { ok: false, msg: 'No such site.' };
  removeFromAssignments(state, c);
  site.security ??= { guards: [], fort: null };
  site.security.guards.push(c.id);
  c.siteId = siteId;
  return { ok: true, msg: `${c.name} now guards ${site.name ?? fieldDef(siteId)?.name ?? siteId}.` };
}

export function buildFortImpl(state, siteId, defId) {
  const site = findSite(state, siteId);
  const def = eqDef(defId);
  if (!site) return { ok: false, msg: 'No such site.' };
  if (!def || def.cat !== 'security') return { ok: false, msg: 'Not a fortification.' };
  if (!isAvailable(def, state)) return { ok: false, msg: `${def.name} isn't available yet.` };
  site.security ??= { guards: [], fort: null };
  if (site.security.fort === defId) return { ok: false, msg: 'Already fortified.' };
  const price = unitPrice(state, def);
  if (!spend(state, price, 'security')) return { ok: false, msg: `Need ${fmt$(price)}.` };
  addItem(state, defId, { type: 'site', id: siteId });
  site.security.fort = defId;
  return { ok: true, msg: `${def.name} built.` };
}

export function regionView(state, regionId) {
  const def = regionDef(regionId);
  if (!def) return null;
  const risk = effectiveRisk(state, regionId);
  const reg = state.regions[regionId] ?? { incidents: [] };
  const fieldIds = Object.keys(state.fields).filter((fid) => regionOfField(fid) === regionId);
  const regionSites = sites(state).filter((w) => fieldIds.includes(w.fieldId));
  const tier = risk.security >= 0.9 ? 0 : risk.security >= 0.7 ? 1 : risk.security >= 0.5 ? 2 : risk.security >= 0.25 ? 3 : 4;
  return {
    def, id: regionId, risk, riskTier: tier, riskLabel: ['Safe', 'Low', 'Medium', 'High', 'War zone'][tier],
    fieldIds, sites: regionSites.map((w) => ({ wellId: w.id, name: w.name, attackChance: attackChance(state, w), guardFactor: guardFactor(state, w, risk) })),
    attackChancePerSite: BASE_HAZARD * (1 - risk.security) ** 2,
    incidents: reg.incidents.slice(-10).reverse(),
    pending: Object.values(state.incidents).filter((i) => i.status === 'pending' && i.regionId === regionId),
    headlines: state.news.filter((n) => n.regionId === regionId || (n.fieldId && fieldIds.includes(n.fieldId))).slice(-5).reverse(),
    transportToMarket: def.transportToMarket ?? 0,
    mercUntil: reg.mercUntil ?? null,
    ...regionM2View(state, regionId), // M2: closure, occupation, threat, strike, partner, bribe, insurance, expropriation, royalty...
  };
}
